import React, { useContext, useId } from "react";
import { Calendar } from "lucide-react";
import { cn } from "@/lib/utils/cn";
import { AIGeneratedBadge } from "./AIGeneratedBadge";
import { useEditableForm } from "@/stores/useEditableFormStore";
import { LabelValueDefaultsContext } from "@/components/common/ui/LabelValue";
import { Typography } from "@/components/common/ui/Typography";

export interface DatePickerProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "type"> {
  ref?: React.Ref<HTMLInputElement>;
  error?: string;
  helperText?: string;
  label?: string;
  formId?: string;
  isAIInferred?: boolean;
  aiInferredReason?: string;
  inferredBadge?: React.ReactNode;
  onFieldClick?: () => void;
  datePickerSize?: "default" | "sm";
  /** Horizontal alignment of the date input text. Defaults to the surrounding
   * `LabelValueDefaultsContext` value, or "left" when none is provided. */
  textAlign?: "left" | "center" | "right";
}

const sizeStyles = {
  default: "px-3 py-2 text-sm rounded-lg",
  sm: "px-2 py-1.5 text-xs rounded-lg h-7",
} as const;

const textAlignClass = {
  left: "text-left",
  center: "text-center",
  right: "text-right",
} as const;

const iconConfig = {
  default: { rightPad: "pr-10", rightPos: "right-3", iconSize: "w-5 h-5", pickerWidth: "[&::-webkit-calendar-picker-indicator]:w-12" },
  sm: { rightPad: "pr-7", rightPos: "right-2", iconSize: "w-4 h-4", pickerWidth: "[&::-webkit-calendar-picker-indicator]:w-8" },
} as const;

export function DatePicker({
  ref,
  error,
  helperText,
  label,
  className = "",
  id,
  required,
  disabled,
  value,
  defaultValue,
  formId,
  isAIInferred,
  aiInferredReason,
  inferredBadge,
  onFieldClick,
  datePickerSize = "default",
  textAlign,
  ...props
}: DatePickerProps) {
  const _id = useId();
  const fieldId = id || props.name || `date-${_id}`;
  const labelValueDefaults = useContext(LabelValueDefaultsContext);
  const effectiveTextAlign =
    textAlign ?? labelValueDefaults.valueAlign ?? "left";
  const {
    forms,
    isEditing: globalIsEditing,
    editableMode: globalEditableMode,
  } = useEditableForm();

  const isEditing = formId
    ? (forms[formId]?.isEditing ?? false)
    : globalIsEditing;

  const editableMode = formId
    ? (forms[formId]?.editableMode ?? false)
    : globalEditableMode;
  const displayValue = value ?? defaultValue;

  if (editableMode && !isEditing) {
    return (
      <div className="w-full">
        {label && (
          <Typography as="label" variant="body" weight="medium" color="text-navigator-text-primary" className="inline-flex items-center gap-1.5 mb-2">
            {label}
            {isAIInferred && (inferredBadge || <AIGeneratedBadge reason={aiInferredReason} />)}
          </Typography>
        )}
        <div
          className={cn(
            "text-gray-900 bg-gray-50 border",
            sizeStyles[datePickerSize],
            textAlignClass[effectiveTextAlign],
            "border-gray-200",
            onFieldClick && "cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 transition-colors",
          )}
          onClick={onFieldClick}
          role={onFieldClick ? "button" : undefined}
          tabIndex={onFieldClick ? 0 : undefined}
          onKeyDown={onFieldClick ? (e) => { if (e.key === "Enter" || e.key === " ") onFieldClick(); } : undefined}
        >
          {displayValue || "—"}
        </div>
      </div>
    );
  }

  const icons = iconConfig[datePickerSize];

  return (
    <div className="w-full">
      {label && (
        <Typography as="label" htmlFor={fieldId} variant="body" weight="medium" color="text-navigator-text-primary" className="block mb-2">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </Typography>
      )}

      <div className="relative">
        <input
          ref={ref}
          id={fieldId}
          type="date"
          name={props.name}
          required={required}
          disabled={disabled}
          {...(value !== undefined ? { value } : { defaultValue })}
          className={cn(
            "w-full border transition-colors",
            sizeStyles[datePickerSize],
            textAlignClass[effectiveTextAlign],
            effectiveTextAlign === "right" &&
              "[&::-webkit-datetime-edit]:block [&::-webkit-datetime-edit]:text-right [&::-webkit-datetime-edit-fields-wrapper]:w-full [&::-webkit-datetime-edit-fields-wrapper]:justify-end",
            icons.rightPad,
            error
              ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
              : "border-gray-200 focus:ring-navigator-primary focus:border-transparent",
            "focus:outline-none focus:ring-2",
            "bg-white text-navigator-text-primary",
            "disabled:bg-gray-50 disabled:cursor-not-allowed",
            "[&::-webkit-calendar-picker-indicator]:absolute",
            "[&::-webkit-calendar-picker-indicator]:right-0",
            icons.pickerWidth,
            "[&::-webkit-calendar-picker-indicator]:h-full",
            "[&::-webkit-calendar-picker-indicator]:opacity-0",
            "[&::-webkit-calendar-picker-indicator]:cursor-pointer",
            className,
          )}
          {...props}
        />
        <div className={cn("absolute top-1/2 transform -translate-y-1/2 pointer-events-none", icons.rightPos)}>
          <Calendar
            className={cn(icons.iconSize, error ? "text-red-400" : "text-gray-400")}
          />
        </div>
      </div>

      {error && <Typography as="p" variant="body" color="error" className="mt-1.5">{error}</Typography>}
      {helperText && !error && (
        <Typography as="p" variant="body" color="light" className="mt-1.5">{helperText}</Typography>
      )}
    </div>
  );
}
